import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import MainTemplate from "../templates/MainTemplate";
import { useNavigate } from "react-router";
import { useGlobalContext } from "../../stores/Global";
import { capitalizeFirstLetter } from "../../helpers/string";
import useBreakpoint from "../../hooks/useBreakpoint";
import EditIcon from "@mui/icons-material/Edit";
import { useEffect, useState } from "react";
import FormInput from "../molecules/FormInput";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { UserService } from "../../services/api";
import QRButton from "../organisms/QRButton";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import CircleIcon from "@mui/icons-material/Circle";

export default function AccountInfoPage() {
  const { t } = useTranslation("AccountInfoPage");
  const breakpoint = useBreakpoint();
  const { context, setContext } = useGlobalContext();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = context;
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState<string>(user?.phone || "");
  const [phone, setPhone] = useState<string>(user?.phone || "");
  const [companyName, setCompanyName] = useState<string>(
    user?.companyName || "",
  );
  const [otpEnabled, setOtpEnabled] = useState<boolean>(
    user?.otpEnabled || false,
  );
  const [emailConfirmation, setEmailConfirmation] = useState<boolean>(
    user?.emailConfirmation || false,
  );
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name);
    setPhone(user.phone);
    setCompanyName(user.companyName);
    setOtpEnabled(user.otpEnabled);
    setEmailConfirmation(user.emailConfirmation);
  }, [user, editMode]);

  const handleUpdateInfo = async () => {
    const res = await UserService.updateMyUser({
      updateMyUserBody: {
        companyName,
        name,
        phone,
        emailConfirmation,
        otpEnabled,
      },
    });
    if (!res?.data?.success) return;
    setEditMode(false);
    setContext({ ...context, user: res.data.data });
  };

  const handleLoadMyQr = async () => {
    const res = await UserService.getMyQR();
    return res?.data?.data;
  };
  const handleCancel = () => {
    navigate(-1);
  };

  const isUserLoading = !user?._id;

  return (
    <MainTemplate>
      {isVisible && (
        <Box
          bgcolor="background.paper"
          display="flex"
          justifyContent="center"
          p={breakpoint.md ? 4 : 2}
          height={1}
          width={1}
        >
          <Paper sx={{ bgcolor: "background.default", py: 4 }}>
            <Box
              mb={2}
              px={6}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant={breakpoint.md ? "h3" : "h6"}
                whiteSpace="initial"
              >
                {t("AccountInfo")}
              </Typography>
              {isUserLoading ? null : editMode ? (
                <Stack direction="row" spacing={1}>
                  <IconButton
                    sx={{ height: 50, width: 50 }}
                    onClick={handleUpdateInfo}
                    color="primary"
                  >
                    <SaveIcon />
                  </IconButton>
                  <IconButton
                    sx={{ height: 50, width: 50 }}
                    onClick={() => setEditMode(false)}
                    color="error"
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
              ) : (
                <IconButton
                  sx={{ height: 50, width: 50 }}
                  onClick={() => setEditMode(true)}
                >
                  <EditIcon />
                </IconButton>
              )}
            </Box>
            {isUserLoading ? (
              <Box
                sx={{
                  minWidth: { xs: 1, md: 600 },
                  px: 4,
                }}
              >
                <Skeleton component={Box} height={170} />
                <Skeleton component={Box} height={170} />
                <Skeleton component={Box} height={170} />
              </Box>
            ) : (
              <TableContainer
                sx={{
                  minWidth: { xs: 1, md: 600 },
                  px: 4,
                }}
              >
                <Table sx={{ td: { borderBottom: "none" } }}>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {t("Name")}
                      </TableCell>
                      <TableCell>
                        {editMode ? (
                          <FormInput
                            field="name"
                            value={name}
                            onChange={(v) => setName(v as string)}
                            label=""
                            type="string"
                            rows={1}
                          />
                        ) : (
                          <Typography variant="body2">{user?.name}</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {t("Username")}
                      </TableCell>
                      <TableCell>{user?.username}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {t("Email")}
                      </TableCell>
                      <TableCell>{user?.email}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {t("Phone")}
                      </TableCell>
                      <TableCell>
                        {editMode ? (
                          <FormInput
                            field="phone"
                            value={phone}
                            onChange={(x) => setPhone(x as string)}
                            label=""
                            type="string"
                            rows={1}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            color={!user?.phone ? "text.secondary" : undefined}
                          >
                            {user?.phone || "N/A"}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {t("Company")}
                      </TableCell>
                      <TableCell>
                        {editMode ? (
                          <FormInput
                            field="companyName"
                            value={companyName}
                            onChange={(x) => setCompanyName(x as string)}
                            label=""
                            type="string"
                            rows={1}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            color={
                              !user?.company ? "text.secondary" : undefined
                            }
                          >
                            {user?.companyName || "—"}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {t("Role")}
                      </TableCell>
                      <TableCell>
                        <Chip label={capitalizeFirstLetter(user?.role || "")} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: "bold", borderBottom: "none" }}
                      >
                        {t("AccountStatus")}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: user?.active
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          borderBottom: "none",
                        }}
                      >
                        {user?.active ? t("Activated") : t("Inactive")}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          fontSize: "large",
                          borderBottom: "none",
                        }}
                      >
                        {t("MultiFactorAuthentication")}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: "bold", borderBottom: "none" }}
                      >
                        {t("Email")}
                      </TableCell>
                      <TableCell>
                        <FormInput
                          field="emailConfirmation"
                          value={emailConfirmation}
                          onChange={(x) => setEmailConfirmation(x as boolean)}
                          label=""
                          type="switch"
                          disabled={!editMode}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: "bold", borderBottom: "none" }}
                      >
                        {t("Authenticator")}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box width={50}>
                            <FormInput
                              field="otpEnabled"
                              value={otpEnabled}
                              onChange={(x) => setOtpEnabled(x as boolean)}
                              label=""
                              type="switch"
                              disabled={!editMode || user?.role !== "user"}
                            />
                          </Box>
                          <QRButton
                            srcFunction={handleLoadMyQr}
                            disabled={!otpEnabled}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Divider sx={{ mt: 2, mb: 2 }} />
            <Box px={6}>
              <Typography fontWeight="bold">
                {t("ChangeYourPassword")}
                <Typography
                  ml={1}
                  component="span"
                  color="primary"
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate("../forgot-password")}
                >
                  {t("ClickHere")}
                </Typography>
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {isUserLoading ? (
        <Box
          sx={{
            maxWidth: breakpoint.md ? 0.6 : 1,
            p: 3,
            margin: "0 auto",
          }}
        >
          <Skeleton component={Box} height={170} />
          <Skeleton component={Box} height={350} />
          <Skeleton component={Box} height={200} />
        </Box>
      ) : (
        <Box bgcolor="background.paper" p={breakpoint.md ? 4 : 2} height={1}>
          <Paper
            sx={{
              bgcolor: "background.default",
              p: 3,
              maxWidth: breakpoint.md ? 0.6 : 1,
              margin: "0 auto",
              alignContent: "center",
            }}
          >
            <Grid container rowGap={4}>
              <Grid size={12} alignItems={"center"}>
                <Stack direction={"row"} spacing={"auto"} alignItems={"center"}>
                  <Stack direction={"column"}>
                    <Typography
                      variant={breakpoint.md ? "h4" : "h5"}
                      whiteSpace="initial"
                      color="primary"
                    >
                      {t("AccountInfo")}
                    </Typography>
                    <Typography variant={breakpoint.md ? "body1" : "subtitle1"}>
                      {t("HeaderSubtitle")}
                    </Typography>
                  </Stack>
                  <Stack direction={"column"}>
                    <Typography variant={breakpoint.md ? "body1" : "subtitle1"}>
                      {t("AccountStatus")}
                    </Typography>
                    <Stack direction={"row"} alignItems={"center"}>
                      <CircleIcon
                        sx={{
                          color: user?.active
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          fontSize: 10,
                          mr: 1,
                        }}
                      />
                      <Typography
                        variant={breakpoint.md ? "body1" : "subtitle1"}
                        sx={{
                          color: user?.active
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          borderBottom: "none",
                        }}
                      >
                        {user?.active ? t("Activated") : t("Inactive")}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Grid>
              <Grid size={12}>
                <Divider />
              </Grid>
              <Grid size={12}>
                <Grid container rowGap={2}>
                  <Grid size={12}>
                    <Typography
                      fontWeight={600}
                      variant={breakpoint.md ? "h6" : "body1"}
                      color="primary"
                    >
                      {t("PersonalDetails")}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <FormInput
                      field="name"
                      value={name}
                      onChange={(v) => setName(v as string)}
                      label={t("Name")}
                      type="string"
                      rows={1}
                      required
                    />
                  </Grid>
                  <Grid size={{ md: 2 }}></Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <FormInput
                      field="username"
                      value={user?.username}
                      onChange={() => {}}
                      label={t("Username")}
                      type="string"
                      rows={1}
                      disabled
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <FormInput
                      field="companyName"
                      value={companyName}
                      onChange={(x) => setCompanyName(x as string)}
                      label={t("Company")}
                      type="string"
                      rows={1}
                      required
                    />
                  </Grid>
                  <Grid size={{ md: 2 }}></Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <FormInput
                      field="email"
                      value={user?.email}
                      onChange={() => {}}
                      label={t("Email")}
                      type="string"
                      rows={1}
                      disabled
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <FormInput
                      field="phone"
                      value={phone}
                      onChange={(x) => setPhone(x as string)}
                      label={t("Phone")}
                      type="string"
                      rows={1}
                    />
                  </Grid>
                  <Grid size={{ md: 2 }}></Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <FormInput
                      field="role"
                      value={capitalizeFirstLetter(user?.role || "")}
                      onChange={() => {}}
                      label={t("Role")}
                      type="string"
                      rows={1}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={12}>
                <Divider />
              </Grid>
              <Grid size={12}>
                <Grid container rowGap={2} alignItems={"center"}>
                  <Grid size={12}>
                    <Typography
                      fontWeight={600}
                      variant={breakpoint.md ? "h6" : "body1"}
                      color="primary"
                    >
                      {t("Security")}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 6, md: 4 }}>
                    <Typography variant={breakpoint.md ? "body1" : "subtitle1"}>
                      {t("EmailAuthentication")}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 8 }}>
                    <FormInput
                      field="emailConfirmation"
                      value={emailConfirmation}
                      onChange={(x) => setEmailConfirmation(x as boolean)}
                      label=""
                      type="switch"
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <Typography variant={breakpoint.md ? "body1" : "subtitle1"}>
                      {t("AuthenticatorApp")}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 8 }}>
                    <Stack direction={"row"} alignItems={"center"} spacing={1}>
                      <Box width={50}>
                        <FormInput
                          field="otpEnabled"
                          value={otpEnabled}
                          onChange={(x) => setOtpEnabled(x as boolean)}
                          label=""
                          type="switch"
                          disabled={user?.role !== "user"}
                        />
                      </Box>
                      {otpEnabled && (
                        <QRButton
                          srcFunction={handleLoadMyQr}
                          disabled={!otpEnabled}
                        />
                      )}
                    </Stack>
                  </Grid>
                  <Grid size={12}>
                    <Button
                      variant={"contained"}
                      onClick={() => navigate("../forgot-password")}
                      size="small"
                    >
                      {t("ChangeYourPassword")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={12}>
                <Divider />
              </Grid>
              <Grid size={12}>
                <Stack direction={"row"} spacing={"auto"}>
                  <Button
                    variant={"outlined"}
                    size="medium"
                    onClick={handleCancel}
                  >
                    {t("Cancel")}
                  </Button>
                  <Button
                    variant={"contained"}
                    color={"success"}
                    size="medium"
                    onClick={handleUpdateInfo}
                  >
                    {t("Save")}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}
    </MainTemplate>
  );
}
